import { useEffect, useState, type FormEvent } from 'react'
import {
  Plus,
  KeyRound,
  Trash2,
  Pencil,
  Loader2,
  ShieldAlert,
  CircleCheck,
  CircleSlash,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../../components/ui/Modal'
import CopyableValue from '../../components/ui/CopyableValue'
import type { CreateUserResponse, Role, UserDetail, UserListItem } from '@toga/shared'
import { useAuth } from '../../contexts/AuthContext'
import IssuerSelect from '../../components/billing/IssuerSelect'

const roleLabel: Record<Role, string> = {
  ADMIN: 'Administrador',
  ABOGADO: 'Abogado',
  PARALEGAL: 'Paralegal',
  LECTURA: 'Solo lectura',
}

const roleAccent: Record<Role, string> = {
  ADMIN: 'bg-toga-500/15 text-toga-200 border-toga-500/30',
  ABOGADO: 'bg-purple-500/15 text-purple-200 border-purple-500/30',
  PARALEGAL: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  LECTURA: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
}

export default function Users() {
  const auth = useAuth()
  const currentUserId = auth.status === 'authenticated' ? auth.user.id : null

  const [users, setUsers] = useState<UserListItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showPasswordFor, setShowPasswordFor] = useState<{
    email: string
    tempPassword: string
  } | null>(null)

  const reload = async () => {
    setError(null)
    try {
      const { users } = await api.get<{ users: UserListItem[] }>('/users')
      setUsers(users)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar usuarios')
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const onResetPassword = async (u: UserListItem) => {
    if (!confirm(`¿Generar una nueva contraseña temporal para ${u.fullName}?`)) return
    try {
      const { tempPassword } = await api.post<{ tempPassword: string }>(
        `/users/${u.id}/reset-password`
      )
      setShowPasswordFor({ email: u.email, tempPassword })
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const onDelete = async (u: UserListItem) => {
    if (
      !confirm(
        `¿Eliminar el usuario ${u.fullName}? Esta acción es reversible solo por la base de datos.`
      )
    )
      return
    try {
      await api.delete(`/users/${u.id}`)
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  /** El PATCH reemplaza el perfil de abogado entero: se parte del actual para no borrar sus datos. */
  const onChangeLawyerIssuer = async (u: UserListItem, issuerId: string | null) => {
    try {
      const { user } = await api.get<{ user: UserDetail }>(`/users/${u.id}`)
      const p = user.lawyerProfile
      await api.patch(`/users/${u.id}`, {
        lawyerProfile: {
          barNumber: p?.barNumber ?? null,
          barAssociation: p?.barAssociation ?? null,
          specialties: p?.specialties ?? [],
          signature: p?.signature ?? null,
          hourlyRateCents: p?.hourlyRateCents ?? null,
          issuerId,
        },
      })
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const onToggleActive = async (u: UserListItem) => {
    try {
      await api.patch(`/users/${u.id}`, { isActive: !u.isActive })
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">Usuarios</h2>
          <p className="t-muted text-sm mt-1">
            Administradores, abogados, paralegales y solo lectura.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga transition-all flex items-center gap-1.5"
        >
          <Plus size={16} /> Nuevo usuario
        </button>
      </header>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
              <th className="px-4 py-3 font-medium">Nombre</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Rol</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3 font-medium">Último acceso</th>
              <th className="px-4 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users === null ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> Cargando…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center t-muted">
                  No hay usuarios.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-base/50 last:border-b-0 hover:bg-hover/50"
                >
                  <td className="px-4 py-3 t-primary font-medium">
                    {u.fullName}
                    {u.id === currentUserId && <span className="text-xs t-faint ml-2">(tú)</span>}
                    {u.mustChangePassword && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-amber-300">
                        <ShieldAlert size={12} /> debe cambiar contraseña
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 t-secondary font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${roleAccent[u.role]}`}
                    >
                      {roleLabel[u.role]}
                    </span>
                    {u.isLawyer && (
                      <div className="mt-1.5">
                        <IssuerSelect
                          value={u.lawyerIssuerId}
                          onChange={(id) => void onChangeLawyerIssuer(u, id)}
                          emptyLabel="Factura con: el del cliente o bufete"
                          className="w-full max-w-[220px] bg-transparent border border-base rounded px-2 py-1 text-[11px] t-secondary"
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.isActive ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                        <CircleCheck size={14} /> activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                        <CircleSlash size={14} /> inactivo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 t-muted text-xs">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('es-ES') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onResetPassword(u)}
                        title="Generar nueva contraseña temporal"
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-300"
                      >
                        <KeyRound size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onToggleActive(u)}
                        title={u.isActive ? 'Desactivar' : 'Activar'}
                        disabled={u.id === currentUserId}
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(u)}
                        title="Eliminar"
                        disabled={u.id === currentUserId}
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreateUserModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(resp) => {
          setShowCreate(false)
          setShowPasswordFor({ email: resp.user.email, tempPassword: resp.tempPassword })
          void reload()
        }}
      />

      <Modal
        open={showPasswordFor !== null}
        onClose={() => setShowPasswordFor(null)}
        title="Contraseña temporal generada"
        description="Copia esta contraseña y entrégala al usuario por un canal seguro. No se mostrará otra vez."
      >
        {showPasswordFor && (
          <div className="space-y-4">
            <CopyableValue label="Email" value={showPasswordFor.email} />
            <CopyableValue label="Contraseña temporal" value={showPasswordFor.tempPassword} />
            <p className="text-xs t-muted">
              El usuario tendrá que cambiarla en su primer inicio de sesión.
            </p>
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowPasswordFor(null)}
                className="px-4 py-2 rounded-lg text-sm bg-toga-500/15 text-toga-200 hover:bg-toga-500/25"
              >
                He copiado la contraseña
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

interface CreateProps {
  open: boolean
  onClose: () => void
  onCreated: (resp: CreateUserResponse) => void
}

function CreateUserModal({ open, onClose, onCreated }: CreateProps) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<Role>('ABOGADO')
  const [barNumber, setBarNumber] = useState('')
  const [barAssociation, setBarAssociation] = useState('')
  const [hourlyRateEuros, setHourlyRateEuros] = useState('')
  const [issuerId, setIssuerId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setEmail('')
    setFullName('')
    setRole('ABOGADO')
    setBarNumber('')
    setBarAssociation('')
    setHourlyRateEuros('')
    setIssuerId(null)
    setError(null)
  }

  const close = () => {
    reset()
    onClose()
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload = {
        email,
        fullName,
        role,
        ...(role === 'ABOGADO'
          ? {
              lawyerProfile: {
                barNumber: barNumber || null,
                barAssociation: barAssociation || null,
                hourlyRateCents: hourlyRateEuros
                  ? Math.round(Number(hourlyRateEuros.replace(',', '.')) * 100)
                  : null,
                issuerId,
              },
            }
          : {}),
      }
      const resp = await api.post<CreateUserResponse>('/users', payload)
      reset()
      onCreated(resp)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError('Error de red')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={close} title="Nuevo usuario" maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">Nombre completo</label>
            <input
              required
              minLength={2}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">Email</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm t-secondary mb-1.5">Rol</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
          >
            <option value="ADMIN">Administrador</option>
            <option value="ABOGADO">Abogado</option>
            <option value="PARALEGAL">Paralegal</option>
            <option value="LECTURA">Solo lectura</option>
          </select>
        </div>

        {role === 'ABOGADO' && (
          <div className="grid grid-cols-3 gap-3 border-t border-base pt-4">
            <div>
              <label className="block text-sm t-secondary mb-1.5">Nº de colegiado</label>
              <input
                value={barNumber}
                onChange={(e) => setBarNumber(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100"
              />
            </div>
            <div>
              <label className="block text-sm t-secondary mb-1.5">Colegio</label>
              <input
                value={barAssociation}
                onChange={(e) => setBarAssociation(e.target.value)}
                placeholder="p. ej. Madrid"
                className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100"
              />
            </div>
            <div>
              <label className="block text-sm t-secondary mb-1.5">Tarifa €/h</label>
              <input
                value={hourlyRateEuros}
                onChange={(e) => setHourlyRateEuros(e.target.value)}
                placeholder="120,00"
                className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 font-mono"
              />
            </div>
            <div className="col-span-3">
              <label className="block text-sm t-secondary mb-1.5">Factura con (emisor)</label>
              <IssuerSelect
                value={issuerId}
                onChange={setIssuerId}
                emptyLabel="El del cliente o el del bufete"
                className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100"
              />
            </div>
          </div>
        )}

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={close}
            className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting || !email || !fullName}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Crear usuario
          </button>
        </div>
      </form>
    </Modal>
  )
}
